import type {
  CalculateRefundRecommendationResponse,
  GetRefundRecommendationHistoryResponse,
  RefundExecutionCode,
  RefundExecutionResponse,
  RefundRecommendationHistoryData
} from './refundRecommendations.types';

const operationIdPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const sha256Pattern = /^[0-9a-f]{64}$/;
const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;

const executionContract = {
  ENTITLEMENT_OPERATION_IN_PROGRESS: [
    409,
    'failed',
    'wait-for-reconciliation',
    'Another refund operation is already in progress for this subscription.'
  ],
  MAXIO_RESULT_AMBIGUOUS: [
    202,
    'unknown',
    'wait-for-reconciliation',
    'The refund result could not be identified safely. Do not submit it again.'
  ],
  MAXIO_RESULT_UNKNOWN_OR_TIMEOUT: [
    202,
    'unknown',
    'wait-for-reconciliation',
    'The refund result is uncertain and is being reconciled. Do not submit it again.'
  ],
  REFUND_EXECUTION_DISABLED: [
    409,
    'failed',
    'none',
    'Automated refund execution is currently disabled.'
  ],
  REFUND_INPUT_CORRECTION_REQUIRED: [
    422,
    'failed',
    'correct-and-start-new-operation',
    'The refund could not be submitted. Correct the input before trying again.'
  ],
  REFUND_OPERATION_PENDING: [
    202,
    'pending',
    'wait-for-reconciliation',
    'Refund submission is being processed. Do not submit it again.'
  ],
  REFUND_PAYMENT_NOT_SETTLED: [
    422,
    'failed',
    'correct-and-start-new-operation',
    'The invoice payment has not settled yet. Wait for it to settle before starting a new refund operation.'
  ],
  REFUND_PRE_DISPATCH_DEPENDENCY_UNAVAILABLE: [
    503,
    'failed',
    'correct-and-start-new-operation',
    'A required refund service is unavailable. Review the calculation again before trying later.'
  ],
  REFUND_RECONFIRMATION_REQUIRED: [
    409,
    'failed',
    'correct-and-start-new-operation',
    'The refund calculation changed. Review it before starting a new operation.'
  ],
  REFUND_SUCCEEDED: [200, 'succeeded', 'none', 'Refund submitted.']
} as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
const isStringMap = (value: unknown): value is Record<string, string> =>
  isRecord(value) && Object.entries(value).every(([key, item]) => isString(key) && isString(item));
const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';
const isInteger = (value: unknown): value is number => Number.isSafeInteger(value);
const isNonNegativeInteger = (value: unknown): value is number => isInteger(value) && value >= 0;
const isPositiveInteger = (value: unknown): value is number => isInteger(value) && value > 0;
const isIsoTimestamp = (value: unknown): value is string =>
  isString(value) &&
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value) &&
  !Number.isNaN(Date.parse(value));
const isIsoDate = (value: unknown): value is string =>
  isString(value) &&
  isoDatePattern.test(value) &&
  (() => {
    const [year, month, day] = value.split('-').map(Number);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return (
      parsed.getUTCFullYear() === year &&
      parsed.getUTCMonth() === month - 1 &&
      parsed.getUTCDate() === day
    );
  })();
const isNullableString = (value: unknown): value is string | null =>
  value === null || isString(value);
const isOneOf = <T extends string>(value: unknown, values: readonly T[]): value is T =>
  typeof value === 'string' && values.includes(value as T);

const hasExactKeys = (
  value: Record<string, unknown>,
  required: readonly string[],
  optional: readonly string[] = []
): boolean => {
  const allowed = new Set([...required, ...optional]);
  return (
    required.every((key) => Object.prototype.hasOwnProperty.call(value, key)) &&
    Object.keys(value).every((key) => allowed.has(key))
  );
};

const parseFailure = (message: string): never => {
  throw new Error(`Invalid refund wire response: ${message}`);
};

const isRecommendation = (value: unknown): boolean => {
  if (value === null) return true;
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['outcome', 'recommendedAmountCents', 'wasOverridden'])
  )
    return false;
  if (value.outcome === 'unavailable')
    return value.recommendedAmountCents === null && value.wasOverridden === true;
  return (
    (value.outcome === 'available' || value.outcome === 'not-eligible') &&
    isNonNegativeInteger(value.recommendedAmountCents) &&
    isBoolean(value.wasOverridden)
  );
};

const isConfirmedRefund = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, [
    'invoiceUid',
    'submittedAmountCents',
    'selectedPaymentTransactionId',
    'maxioRefundUid',
    'maxioRefundTransactionId'
  ]) &&
  isString(value.invoiceUid) &&
  isNonNegativeInteger(value.submittedAmountCents) &&
  isPositiveInteger(value.selectedPaymentTransactionId) &&
  isString(value.maxioRefundUid) &&
  isPositiveInteger(value.maxioRefundTransactionId);

export const parseRefundExecutionResponse = (
  input: unknown,
  httpStatus: number,
  expected?: { invoiceUid?: string; operationId: string }
): RefundExecutionResponse => {
  if (
    !isRecord(input) ||
    !hasExactKeys(input, [
      'operationId',
      'executionStatus',
      'executionCode',
      'serializationScope',
      'nextAction',
      'replayed',
      'message',
      'recommendation',
      'submittedAmountCents',
      'refund',
      'billingHistoryItem',
      'enrichmentStatus'
    ]) ||
    !isString(input.executionCode) ||
    !(input.executionCode in executionContract)
  ) {
    return parseFailure('execution envelope shape');
  }

  const code = input.executionCode as RefundExecutionCode;
  const [expectedHttp, expectedStatus, expectedAction, expectedMessage] = executionContract[code];
  const baseIsValid =
    httpStatus === expectedHttp &&
    input.executionStatus === expectedStatus &&
    input.nextAction === expectedAction &&
    input.message === expectedMessage &&
    isString(input.operationId) &&
    operationIdPattern.test(input.operationId) &&
    (input.serializationScope === null ||
      input.serializationScope === 'entitlement' ||
      input.serializationScope === 'invoice-fallback') &&
    isBoolean(input.replayed) &&
    isRecommendation(input.recommendation) &&
    isNonNegativeInteger(input.submittedAmountCents) &&
    input.billingHistoryItem === null &&
    isOneOf(input.enrichmentStatus, [
      'not-required',
      'pending',
      'in-progress',
      'succeeded',
      'failed'
    ]);
  const refundIsValid =
    code === 'REFUND_SUCCEEDED' ? isConfirmedRefund(input.refund) : input.refund === null;

  if (!baseIsValid || !refundIsValid) return parseFailure('execution contract mismatch');
  if (expected !== undefined && input.operationId !== expected.operationId) {
    return parseFailure('execution operation mismatch');
  }
  if (
    code === 'REFUND_SUCCEEDED' &&
    (input.refund as Record<string, unknown>).submittedAmountCents !== input.submittedAmountCents
  ) {
    return parseFailure('confirmed refund amount mismatch');
  }
  if (
    code === 'REFUND_SUCCEEDED' &&
    expected?.invoiceUid !== undefined &&
    (input.refund as Record<string, unknown>).invoiceUid !== expected.invoiceUid
  ) {
    return parseFailure('confirmed refund invoice mismatch');
  }
  return input as RefundExecutionResponse;
};

const auditOperationKeys = [
  'approvingSupervisorName',
  'operationId',
  'normalizedRequest',
  'selectedPaymentTransactionId',
  'serializationScope',
  'reservationActive',
  'reservationActivatedAt',
  'attemptStartedAt',
  'stateChangedAt',
  'terminalAt',
  'externalRefund',
  'reconciliationStatus',
  'reconciliationUpdatedAt',
  'enrichmentStatus',
  'enrichmentUpdatedAt'
] as const;

const isNormalizedRequest = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, [
    'cancellationEffectiveDate',
    'submittedAmountCents',
    'note',
    'reason',
    'reviewedCalculationSource',
    'expectedRecommendationOutcome',
    'expectedSerializationScope',
    'manualUnavailableConfirmed'
  ]) &&
  isIsoDate(value.cancellationEffectiveDate) &&
  isNonNegativeInteger(value.submittedAmountCents) &&
  isString(value.note) &&
  isNullableString(value.reason) &&
  isOneOf(value.reviewedCalculationSource, ['server', 'calculation-request-failed']) &&
  isOneOf(value.expectedRecommendationOutcome, ['available', 'not-eligible', 'unavailable']) &&
  isOneOf(value.expectedSerializationScope, ['entitlement', 'invoice-fallback']) &&
  isBoolean(value.manualUnavailableConfirmed);

const isExternalRefund = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, [
    'invoiceUid',
    'maxioRefundUid',
    'maxioRefundTransactionId',
    'selectedPaymentTransactionId',
    'submittedAmountCents',
    'transactionTime'
  ]) &&
  isString(value.invoiceUid) &&
  isString(value.maxioRefundUid) &&
  isPositiveInteger(value.maxioRefundTransactionId) &&
  isPositiveInteger(value.selectedPaymentTransactionId) &&
  isNonNegativeInteger(value.submittedAmountCents) &&
  isIsoTimestamp(value.transactionTime);

const hasValidOperationEvidence = (value: Record<string, unknown>): boolean =>
  (value.approvingSupervisorName === undefined ||
    (isString(value.approvingSupervisorName) && value.approvingSupervisorName.length > 0)) &&
  (value.operationId === undefined || isString(value.operationId)) &&
  (value.normalizedRequest === undefined || isNormalizedRequest(value.normalizedRequest)) &&
  (value.selectedPaymentTransactionId === undefined ||
    isPositiveInteger(value.selectedPaymentTransactionId)) &&
  (value.serializationScope === undefined ||
    isOneOf(value.serializationScope, ['entitlement', 'invoice-fallback'])) &&
  (value.reservationActive === undefined || isBoolean(value.reservationActive)) &&
  [
    'reservationActivatedAt',
    'attemptStartedAt',
    'stateChangedAt',
    'terminalAt',
    'reconciliationUpdatedAt',
    'enrichmentUpdatedAt'
  ].every((key) => value[key] === undefined || isIsoTimestamp(value[key])) &&
  (value.externalRefund === undefined || isExternalRefund(value.externalRefund)) &&
  (value.reconciliationStatus === undefined ||
    isOneOf(value.reconciliationStatus, [
      'not-required',
      'pending',
      'in-progress',
      'resolved',
      'manual-review-required'
    ])) &&
  (value.enrichmentStatus === undefined ||
    isOneOf(value.enrichmentStatus, [
      'not-required',
      'pending',
      'in-progress',
      'succeeded',
      'failed'
    ]));

const isPricing = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(
    value,
    ['pricingRecordId', 'pricingVersion', 'effectiveStart', 'effectiveEnd'],
    ['artifactId', 'artifactVersion', 'sourceSha256']
  ) &&
  isString(value.pricingRecordId) &&
  isString(value.pricingVersion) &&
  isIsoTimestamp(value.effectiveStart) &&
  isIsoTimestamp(value.effectiveEnd) &&
  (value.artifactId === undefined || isString(value.artifactId)) &&
  (value.artifactVersion === undefined || isString(value.artifactVersion)) &&
  (value.sourceSha256 === undefined ||
    (isString(value.sourceSha256) && sha256Pattern.test(value.sourceSha256)));

const isPriorRefund = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, ['invoiceUid', 'refundId', 'priorRefundAmountCents']) &&
  isString(value.invoiceUid) &&
  isString(value.refundId) &&
  isNonNegativeInteger(value.priorRefundAmountCents);

const baseAuditKeys = [
  '_id',
  'patientId',
  'invoiceUid',
  'explanation',
  'cancellationEffectiveDate',
  'submittedAmountCents',
  'wasOverridden',
  'agentId',
  'executionStatus',
  'createdAt',
  'updatedAt'
] as const;
const failureReasons = [
  'MAXIO_REJECTED',
  'EXECUTION_FAILED',
  'MAXIO_RESULT_UNKNOWN_OR_TIMEOUT',
  'ENTITLEMENT_OPERATION_IN_PROGRESS',
  'REFUND_EXECUTION_DISABLED',
  'REFUND_RECONFIRMATION_REQUIRED',
  'REFUND_INPUT_CORRECTION_REQUIRED',
  'REFUND_PAYMENT_NOT_SETTLED',
  'REFUND_PRE_DISPATCH_DEPENDENCY_UNAVAILABLE'
] as const;
const unavailableReasons = [
  'ALLOCATION_IDENTITY_NOT_FOUND',
  'ARITHMETIC_OVERFLOW',
  'BUNDLED_MONTHLY_AMOUNT_NOT_UNIQUE',
  'CANCELLATION_BEFORE_PAYMENT',
  'CONFIG_READ_FAILED',
  'ENTITLEMENT_METADATA_INVALID',
  'ENTITLEMENT_NOT_FOUND',
  'ENTITLEMENT_NOT_UNIQUE',
  'INVALID_PAYMENT',
  'INVALID_CANCELLATION_DATE',
  'INVALID_PLANEX_ORDER_ID',
  'INVALID_PLANEX_ORDERS',
  'INVALID_PLANEX_PRESCRIPTIONS',
  'INVALID_PRICE_POINT',
  'INVALID_PURCHASE_LINE',
  'INVALID_SHIPMENT_DATE',
  'INVALID_SOURCE_MONETARY_AMOUNT',
  'INVOICE_NOT_PAID',
  'MAXIO_READ_FAILED',
  'NEGATIVE_CARE_ALLOCATION',
  'PAYMENT_NOT_FOUND',
  'PATIENT_OWNERSHIP_INVALID',
  'PLANEX_READ_FAILED',
  'PRICE_POINT_NOT_FOUND',
  'PRICING_NOT_FOUND',
  'PRICING_NOT_UNIQUE',
  'RELATED_REFUND_INVALID',
  'REPLACEMENT_MARKER_MISSING_OR_INVALID',
  'SHIPMENT_REPRICE_NOT_FOUND',
  'SHIPMENT_REPRICE_NOT_UNIQUE'
] as const;

const isCalculationSelectedPayment = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, [
    'transactionId',
    'appliedAmountCents',
    'refundedAmountCents',
    'refundableAmountCents',
    'appliedAmountDisplay',
    'refundedAmountDisplay',
    'refundableAmountDisplay'
  ]) &&
  isPositiveInteger(value.transactionId) &&
  isNonNegativeInteger(value.appliedAmountCents) &&
  isNonNegativeInteger(value.refundedAmountCents) &&
  isNonNegativeInteger(value.refundableAmountCents) &&
  isString(value.appliedAmountDisplay) &&
  isString(value.refundedAmountDisplay) &&
  isString(value.refundableAmountDisplay);

const isCalculationPricing = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, [
    'pricingRecordId',
    'pricingVersion',
    'artifactId',
    'artifactVersion',
    'sourceSha256',
    'effectiveStart',
    'effectiveEnd'
  ]) &&
  isString(value.pricingRecordId) &&
  isString(value.pricingVersion) &&
  isString(value.artifactId) &&
  isString(value.artifactVersion) &&
  isString(value.sourceSha256) &&
  sha256Pattern.test(value.sourceSha256) &&
  isIsoDate(value.effectiveStart) &&
  isIsoDate(value.effectiveEnd);

const isCalculationPriorRefund = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, ['invoiceUid', 'refundUid', 'amountCents', 'amountDisplay']) &&
  isString(value.invoiceUid) &&
  isString(value.refundUid) &&
  isNonNegativeInteger(value.amountCents) &&
  isString(value.amountDisplay);

const isCalculationIncludedShipment = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, [
    'orderId',
    'shipmentDate',
    'qualifyingShipmentCount',
    'repriceCents',
    'repriceDisplay'
  ]) &&
  isString(value.orderId) &&
  isIsoDate(value.shipmentDate) &&
  isPositiveInteger(value.qualifyingShipmentCount) &&
  isNonNegativeInteger(value.repriceCents) &&
  isString(value.repriceDisplay);

const isCalculationQualifyingShipment = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, ['orderId', 'shipmentDate']) &&
  isString(value.orderId) &&
  isIsoDate(value.shipmentDate);

const calculationBreakdownKeys = [
  'invoiceAmountCents',
  'discountAmountCents',
  'paymentDate',
  'cancellationEffectiveDate',
  'termMonths',
  'termEndExclusive',
  'termDays',
  'elapsedCareDays',
  'dailyCareRate',
  'medicationAllocationCents',
  'careAllocationCents',
  'remainingCareCents',
  'shipmentRepricesCents',
  'consumedMedicationCents',
  'remainingMedicationCents',
  'priorRefundsCents',
  'rawRefundCents',
  'economicRecommendationCents',
  'maxioRefundableCents',
  'recommendedAmountCents',
  'includedShipments',
  'qualifyingShipments',
  'excludedReplacementOrderIds',
  'priorRefunds',
  'invoiceAmountDisplay',
  'discountAmountDisplay',
  'medicationAllocationDisplay',
  'careAllocationDisplay',
  'remainingCareDisplay',
  'consumedMedicationDisplay',
  'remainingMedicationDisplay',
  'priorRefundsDisplay',
  'rawRefundDisplay',
  'economicRecommendationDisplay',
  'maxioRefundableDisplay',
  'recommendedAmountDisplay'
] as const;

const isCalculationBreakdown = (value: unknown): boolean => {
  if (!isRecord(value) || !hasExactKeys(value, calculationBreakdownKeys)) return false;
  const nonnegative = [
    'invoiceAmountCents',
    'discountAmountCents',
    'elapsedCareDays',
    'medicationAllocationCents',
    'remainingCareCents',
    'consumedMedicationCents',
    'priorRefundsCents',
    'economicRecommendationCents',
    'maxioRefundableCents',
    'recommendedAmountCents'
  ];
  const signed = ['careAllocationCents', 'remainingMedicationCents', 'rawRefundCents'];
  const displays = calculationBreakdownKeys.filter((key) => key.endsWith('Display'));
  return (
    nonnegative.every((key) => isNonNegativeInteger(value[key])) &&
    signed.every((key) => isInteger(value[key])) &&
    isIsoDate(value.paymentDate) &&
    isIsoDate(value.cancellationEffectiveDate) &&
    (value.termMonths === 3 || value.termMonths === 6 || value.termMonths === 12) &&
    isIsoDate(value.termEndExclusive) &&
    isPositiveInteger(value.termDays) &&
    isRecord(value.dailyCareRate) &&
    hasExactKeys(value.dailyCareRate, ['numeratorCents', 'denominatorDays', 'display']) &&
    isInteger(value.dailyCareRate.numeratorCents) &&
    isPositiveInteger(value.dailyCareRate.denominatorDays) &&
    isString(value.dailyCareRate.display) &&
    Array.isArray(value.shipmentRepricesCents) &&
    value.shipmentRepricesCents.every(isNonNegativeInteger) &&
    Array.isArray(value.includedShipments) &&
    value.includedShipments.every(isCalculationIncludedShipment) &&
    Array.isArray(value.qualifyingShipments) &&
    value.qualifyingShipments.every(isCalculationQualifyingShipment) &&
    Array.isArray(value.excludedReplacementOrderIds) &&
    value.excludedReplacementOrderIds.every(isString) &&
    Array.isArray(value.priorRefunds) &&
    value.priorRefunds.every(isCalculationPriorRefund) &&
    displays.every((key) => isString(value[key]))
  );
};

const isCalculationKnownInput = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(
    value,
    [
      'invoiceAmountCents',
      'discountAmountCents',
      'paymentDate',
      'cancellationEffectiveDate',
      'selectedPayment'
    ],
    ['termMonths', 'bundledMonthlyMedicationAmountCents']
  ) &&
  isNonNegativeInteger(value.invoiceAmountCents) &&
  isNonNegativeInteger(value.discountAmountCents) &&
  isIsoDate(value.paymentDate) &&
  isIsoDate(value.cancellationEffectiveDate) &&
  isCalculationSelectedPayment(value.selectedPayment) &&
  (value.termMonths === undefined ||
    value.termMonths === 3 ||
    value.termMonths === 6 ||
    value.termMonths === 12) &&
  (value.bundledMonthlyMedicationAmountCents === undefined ||
    isNonNegativeInteger(value.bundledMonthlyMedicationAmountCents));

const isCalculationUnavailableSnapshot = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(
    value,
    [
      'invoiceAmountCents',
      'discountAmountCents',
      'paymentDate',
      'cancellationEffectiveDate',
      'termMonths',
      'bundledMonthlyMedicationAmountCents',
      'shipmentRepricesCents',
      'priorRefundsCents',
      'maxioRefundableCents'
    ],
    ['medicationAllocationCents', 'careAllocationCents']
  ) &&
  [
    'invoiceAmountCents',
    'discountAmountCents',
    'bundledMonthlyMedicationAmountCents',
    'priorRefundsCents',
    'maxioRefundableCents'
  ].every((key) => isNonNegativeInteger(value[key])) &&
  isIsoDate(value.paymentDate) &&
  isIsoDate(value.cancellationEffectiveDate) &&
  (value.termMonths === 3 || value.termMonths === 6 || value.termMonths === 12) &&
  Array.isArray(value.shipmentRepricesCents) &&
  value.shipmentRepricesCents.every(isNonNegativeInteger) &&
  (value.medicationAllocationCents === undefined ||
    isNonNegativeInteger(value.medicationAllocationCents)) &&
  (value.careAllocationCents === undefined || isInteger(value.careAllocationCents));

const unavailableBreakdownKeys = [
  'medicationAllocationCents',
  'careAllocationCents',
  'remainingCareCents',
  'consumedMedicationCents',
  'remainingMedicationCents',
  'priorRefundsCents',
  'rawRefundCents',
  'economicRecommendationCents',
  'maxioRefundableCents',
  'recommendedAmountCents'
] as const;
const isCalculationUnavailableBreakdown = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, [], unavailableBreakdownKeys) &&
  unavailableBreakdownKeys.every(
    (key) =>
      value[key] === undefined ||
      (['careAllocationCents', 'remainingMedicationCents', 'rawRefundCents'].includes(key)
        ? isInteger(value[key])
        : isNonNegativeInteger(value[key]))
  );

const isCalculationShipmentEvidence = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, [
    'qualifyingShipments',
    'includedShipments',
    'excludedReplacementOrderIds'
  ]) &&
  Array.isArray(value.qualifyingShipments) &&
  value.qualifyingShipments.every(isCalculationQualifyingShipment) &&
  Array.isArray(value.includedShipments) &&
  value.includedShipments.every(isCalculationIncludedShipment) &&
  Array.isArray(value.excludedReplacementOrderIds) &&
  value.excludedReplacementOrderIds.every(isString);

const calculatedResultKeys = [
  'outcome',
  'serializationScope',
  'manualEntryAllowed',
  'explanation',
  'recommendedAmountCents',
  'recommendedAmountDisplay',
  'maxioSubscriptionId',
  'entitlementId',
  'pricePointHandle',
  'termMonths',
  'appliedPricing',
  'priorRefunds',
  'selectedPayment',
  'breakdown'
] as const;

const isCalculatedResult = (value: Record<string, unknown>): boolean =>
  hasExactKeys(value, calculatedResultKeys) &&
  (value.outcome === 'available' || value.outcome === 'not-eligible') &&
  value.serializationScope === 'entitlement' &&
  value.manualEntryAllowed === true &&
  isString(value.explanation) &&
  isNonNegativeInteger(value.recommendedAmountCents) &&
  isString(value.recommendedAmountDisplay) &&
  isString(value.maxioSubscriptionId) &&
  isString(value.entitlementId) &&
  isString(value.pricePointHandle) &&
  (value.termMonths === 3 || value.termMonths === 6 || value.termMonths === 12) &&
  Array.isArray(value.appliedPricing) &&
  value.appliedPricing.every(isCalculationPricing) &&
  Array.isArray(value.priorRefunds) &&
  value.priorRefunds.every(isCalculationPriorRefund) &&
  isCalculationSelectedPayment(value.selectedPayment) &&
  isCalculationBreakdown(value.breakdown);

const unavailableResultRequiredKeys = [
  'outcome',
  'serializationScope',
  'reason',
  'manualEntryAllowed',
  'explanation',
  'recommendedAmountCents',
  'recommendedAmountDisplay'
] as const;
const unavailableResultOptionalKeys = [
  'maxioSubscriptionId',
  'entitlementId',
  'pricePointHandle',
  'termMonths',
  'appliedPricing',
  'selectedPayment',
  'input',
  'priorRefunds',
  'unavailableSnapshot',
  'breakdown',
  'shipmentEvidence'
] as const;

const isUnavailableResult = (value: Record<string, unknown>): boolean =>
  hasExactKeys(value, unavailableResultRequiredKeys, unavailableResultOptionalKeys) &&
  value.outcome === 'unavailable' &&
  isOneOf(value.serializationScope, ['entitlement', 'invoice-fallback']) &&
  isOneOf(value.reason, unavailableReasons) &&
  value.manualEntryAllowed === true &&
  isString(value.explanation) &&
  value.recommendedAmountCents === null &&
  value.recommendedAmountDisplay === null &&
  ['maxioSubscriptionId', 'entitlementId', 'pricePointHandle'].every(
    (key) => value[key] === undefined || isString(value[key])
  ) &&
  (value.termMonths === undefined ||
    value.termMonths === 3 ||
    value.termMonths === 6 ||
    value.termMonths === 12) &&
  (value.appliedPricing === undefined ||
    (Array.isArray(value.appliedPricing) && value.appliedPricing.every(isCalculationPricing))) &&
  (value.selectedPayment === undefined || isCalculationSelectedPayment(value.selectedPayment)) &&
  (value.input === undefined || isCalculationKnownInput(value.input)) &&
  (value.priorRefunds === undefined ||
    (Array.isArray(value.priorRefunds) && value.priorRefunds.every(isCalculationPriorRefund))) &&
  (value.unavailableSnapshot === undefined ||
    isCalculationUnavailableSnapshot(value.unavailableSnapshot)) &&
  (value.breakdown === undefined || isCalculationUnavailableBreakdown(value.breakdown)) &&
  (value.shipmentEvidence === undefined || isCalculationShipmentEvidence(value.shipmentEvidence));

export const parseCalculateRefundRecommendationResponse = (
  input: unknown
): CalculateRefundRecommendationResponse => {
  if (
    !isRecord(input) ||
    !hasExactKeys(input, ['message', 'errorCode', 'data', 'info']) ||
    typeof input.message !== 'string' ||
    !isInteger(input.errorCode) ||
    !isRecord(input.info) ||
    !isRecord(input.data) ||
    !(isCalculatedResult(input.data) || isUnavailableResult(input.data))
  ) {
    return parseFailure('calculation response contract mismatch');
  }
  return input as CalculateRefundRecommendationResponse;
};

const auditInputKeys = [
  'originalInvoiceAmountCents',
  'couponDiscountAmountCents',
  'bundledMonthlyMedicationAmountCents',
  'selectedPaymentTransactionId',
  'selectedPaymentAmountCents',
  'selectedPaymentRefundedCents',
  'dateRule',
  'paymentDate',
  'termMonths',
  'termStartDate',
  'termEndDate',
  'termDays',
  'elapsedCareDays'
] as const;
const isAuditInput = (value: unknown, partial: boolean): boolean => {
  if (!isRecord(value)) return false;
  if (!hasExactKeys(value, partial ? [] : auditInputKeys, partial ? auditInputKeys : []))
    return false;
  const nonnegative = [
    'originalInvoiceAmountCents',
    'couponDiscountAmountCents',
    'bundledMonthlyMedicationAmountCents',
    'selectedPaymentRefundedCents',
    'elapsedCareDays'
  ];
  const positive = ['selectedPaymentTransactionId', 'selectedPaymentAmountCents', 'termDays'];
  const dates = ['paymentDate', 'termStartDate', 'termEndDate'];
  return (
    nonnegative.every((key) => value[key] === undefined || isNonNegativeInteger(value[key])) &&
    positive.every((key) => value[key] === undefined || isPositiveInteger(value[key])) &&
    dates.every((key) => value[key] === undefined || isIsoTimestamp(value[key])) &&
    (value.dateRule === undefined || value.dateRule === 'UTC_CALENDAR_INCLUSIVE_CANCELLATION') &&
    (value.termMonths === undefined ||
      value.termMonths === 3 ||
      value.termMonths === 6 ||
      value.termMonths === 12)
  );
};

const includedShipmentKeys = [
  'orderId',
  'shipmentDate',
  'qualifyingShipmentCount',
  'repriceMonthlyAmountCents'
] as const;
const isIncludedShipment = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, includedShipmentKeys) &&
  isString(value.orderId) &&
  isIsoTimestamp(value.shipmentDate) &&
  isPositiveInteger(value.qualifyingShipmentCount) &&
  isNonNegativeInteger(value.repriceMonthlyAmountCents);
const isQualifyingShipment = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, ['orderId', 'shipmentDate']) &&
  isString(value.orderId) &&
  isIsoTimestamp(value.shipmentDate);
const breakdownKeys = [
  'medicationAllocationCents',
  'careAllocationCents',
  'remainingCareCents',
  'includedShipments',
  'excludedReplacementOrderIds',
  'consumedMedicationCents',
  'remainingMedicationCents',
  'priorRefundAmountCents',
  'rawRefundCents',
  'maxioRefundableCashCents',
  'economicRecommendationCents',
  'recommendedAmountCents'
] as const;
const isBreakdown = (value: unknown, partial: boolean): boolean => {
  if (
    !isRecord(value) ||
    !hasExactKeys(
      value,
      partial ? [] : breakdownKeys,
      partial ? [...breakdownKeys, 'qualifyingShipments'] : []
    )
  )
    return false;
  const nonnegative = [
    'medicationAllocationCents',
    'remainingCareCents',
    'consumedMedicationCents',
    'priorRefundAmountCents',
    'maxioRefundableCashCents',
    'economicRecommendationCents',
    'recommendedAmountCents'
  ];
  const signed = ['careAllocationCents', 'remainingMedicationCents', 'rawRefundCents'];
  return (
    nonnegative.every((key) => value[key] === undefined || isNonNegativeInteger(value[key])) &&
    signed.every((key) => value[key] === undefined || isInteger(value[key])) &&
    (value.includedShipments === undefined ||
      (Array.isArray(value.includedShipments) &&
        value.includedShipments.every(isIncludedShipment))) &&
    (value.excludedReplacementOrderIds === undefined ||
      (Array.isArray(value.excludedReplacementOrderIds) &&
        value.excludedReplacementOrderIds.every(isString))) &&
    (value.qualifyingShipments === undefined ||
      (Array.isArray(value.qualifyingShipments) &&
        value.qualifyingShipments.every(isQualifyingShipment)))
  );
};
const commonAuditValid = (value: Record<string, unknown>): boolean =>
  ['_id', 'patientId', 'invoiceUid', 'explanation', 'agentId'].every((key) =>
    isString(value[key])
  ) &&
  isIsoTimestamp(value.cancellationEffectiveDate) &&
  isNonNegativeInteger(value.submittedAmountCents) &&
  isBoolean(value.wasOverridden) &&
  isOneOf(value.executionStatus, ['pending', 'unknown', 'succeeded', 'failed']) &&
  (value.failureReason === undefined || isOneOf(value.failureReason, failureReasons)) &&
  isIsoTimestamp(value.createdAt) &&
  isIsoTimestamp(value.updatedAt) &&
  hasValidOperationEvidence(value);

const isPrecalculationAudit = (value: Record<string, unknown>): boolean =>
  hasExactKeys(
    value,
    [
      '_id',
      'patientId',
      'invoiceUid',
      'recommendation',
      'cancellationEffectiveDate',
      'submittedAmountCents',
      'agentId',
      'executionStatus',
      'failureReason',
      'createdAt',
      'updatedAt'
    ],
    auditOperationKeys
  ) &&
  value.recommendation === null &&
  value.executionStatus === 'failed' &&
  value.failureReason === 'REFUND_EXECUTION_DISABLED' &&
  ['_id', 'patientId', 'invoiceUid', 'agentId'].every((key) => isString(value[key])) &&
  isIsoTimestamp(value.cancellationEffectiveDate) &&
  isNonNegativeInteger(value.submittedAmountCents) &&
  isIsoTimestamp(value.createdAt) &&
  isIsoTimestamp(value.updatedAt) &&
  hasValidOperationEvidence(value);

const isAudit = (value: unknown): boolean => {
  if (!isRecord(value)) return false;
  if (Object.prototype.hasOwnProperty.call(value, 'recommendation'))
    return isPrecalculationAudit(value);
  const calculatedKeys = [
    'maxioSubscriptionId',
    'entitlementId',
    'input',
    'breakdown',
    'appliedPricing',
    'priorRefunds'
  ];
  const isUnavailable = value.outcome === 'unavailable';
  if (
    !hasExactKeys(
      value,
      [...baseAuditKeys, 'outcome'],
      [...auditOperationKeys, ...calculatedKeys, 'unavailableReason', 'failureReason']
    )
  )
    return false;
  if (
    !commonAuditValid(value) ||
    !isOneOf(value.outcome, ['available', 'not-eligible', 'unavailable'])
  )
    return false;
  if (isUnavailable && !isOneOf(value.unavailableReason, unavailableReasons)) return false;
  if (
    !isUnavailable &&
    (!isString(value.maxioSubscriptionId) ||
      !isString(value.entitlementId) ||
      !isAuditInput(value.input, false) ||
      !isBreakdown(value.breakdown, false) ||
      !Array.isArray(value.appliedPricing) ||
      !Array.isArray(value.priorRefunds))
  )
    return false;
  if (
    isUnavailable &&
    ((value.input !== undefined && !isAuditInput(value.input, true)) ||
      (value.breakdown !== undefined && !isBreakdown(value.breakdown, true)))
  )
    return false;
  if (
    value.appliedPricing !== undefined &&
    (!Array.isArray(value.appliedPricing) || !value.appliedPricing.every(isPricing))
  )
    return false;
  if (
    value.priorRefunds !== undefined &&
    (!Array.isArray(value.priorRefunds) || !value.priorRefunds.every(isPriorRefund))
  )
    return false;
  return true;
};

const isActiveOperation = (value: unknown): boolean =>
  value === null ||
  (isRecord(value) &&
    hasExactKeys(value, [
      'operationId',
      'invoiceUid',
      'executionStatus',
      'executionCode',
      'submittedAmountCents',
      'serializationScope',
      'createdAt',
      'updatedAt'
    ]) &&
    isString(value.operationId) &&
    isString(value.invoiceUid) &&
    isOneOf(value.executionStatus, ['pending', 'unknown']) &&
    isOneOf(value.executionCode, [
      'REFUND_OPERATION_PENDING',
      'MAXIO_RESULT_UNKNOWN_OR_TIMEOUT',
      'MAXIO_RESULT_AMBIGUOUS'
    ]) &&
    ((value.executionStatus === 'pending' && value.executionCode === 'REFUND_OPERATION_PENDING') ||
      (value.executionStatus === 'unknown' &&
        (value.executionCode === 'MAXIO_RESULT_UNKNOWN_OR_TIMEOUT' ||
          value.executionCode === 'MAXIO_RESULT_AMBIGUOUS'))) &&
    isNonNegativeInteger(value.submittedAmountCents) &&
    (value.serializationScope === null ||
      isOneOf(value.serializationScope, ['entitlement', 'invoice-fallback'])) &&
    isIsoTimestamp(value.createdAt) &&
    isIsoTimestamp(value.updatedAt));

export const parseRefundRecommendationHistoryResponse = (
  input: unknown,
  expectedInvoiceUid: string
): GetRefundRecommendationHistoryResponse => {
  if (
    !isRecord(input) ||
    !hasExactKeys(input, ['message', 'errorCode', 'data', 'info']) ||
    typeof input.message !== 'string' ||
    !isInteger(input.errorCode) ||
    !isRecord(input.info) ||
    !isRecord(input.data)
  ) {
    return parseFailure('history envelope shape');
  }
  const data = input.data;
  if (
    !hasExactKeys(data, [
      'requestedInvoiceUid',
      'items',
      'nextCursor',
      'activeOperation',
      'agentsById'
    ]) ||
    data.requestedInvoiceUid !== expectedInvoiceUid ||
    !Array.isArray(data.items) ||
    !(data.nextCursor === null || isString(data.nextCursor)) ||
    !isActiveOperation(data.activeOperation) ||
    !isStringMap(data.agentsById)
  ) {
    return parseFailure('history data contract mismatch');
  }
  if (
    !data.items.every(isAudit) ||
    !data.items.every((item) => isRecord(item) && item.invoiceUid === expectedInvoiceUid)
  ) {
    return {
      data: {
        activeOperation: data.activeOperation as RefundRecommendationHistoryData['activeOperation'],
        agentsById: data.agentsById,
        historyIntegrityValid: false,
        items: [],
        nextCursor: null,
        requestedInvoiceUid: data.requestedInvoiceUid
      },
      errorCode: input.errorCode,
      info: input.info,
      message: input.message
    };
  }
  return input as GetRefundRecommendationHistoryResponse;
};
